/**
 * An option represent the classes and fares of a trip
 */

import { sortBy, uniqBy } from "lodash";
import { createCachedSelector } from "re-reselect";

import type {
  RelatedPublicSegment,
  RelatedPublicSegmentV4,
  RelatedPublicTrip,
  RelatedPublicTripV4,
  PublicFare,
  PublicFareV4
} from "@busbud/core-types/dist/lib/poll-service/api";

import type { DisplayedPriceConfig } from "@app/helpers/price";
import { shouldIncludeFees } from "@app/helpers/price";
import { isReturn } from "@app/helpers/search";
import { getDisplayPrice } from "@app/helpers/udm/get-display-price";
import { getLocalizedPrice } from "@app/helpers/udm/get-localized-price";
import { isUDMV4 } from "@app/helpers/udm/v4-migration-helper/is-udm-v4";
import type {
  TripOption,
  TripOptionsList,
  TripOptionsMatrix
} from "@app/types/results-types";
import type { SearchInfo } from "@app/types/search-types";

import type { LegCard } from "./get-leg-cards";

import { getLegCardById } from "./get-leg-card-by-id";
import {
  getLegSourceNamesFromTripPrices,
  getSearchedLeg,
  getSegmentsFromTripLeg
} from "../../../../helpers/udm";
import { isTripSoldOut as isTripSoldOutHelper } from "../../../../helpers/udm/is-trip-sold-out";
import {
  getSearch,
  getTrips,
  getTripsFares,
  getTripsSegments
} from "../state-accessors";
import { getTripIdsByLegCardIdAndJourneyId } from "./get-trip-ids-by-leg-card-id-and-journey-id";
import { getDisplayedPriceConfig } from "../get-displayed-price-config";

type SimpleLegOption = {
  class_xid: string;
  fare_id: string;
  trip_id: string;
};

/**
 *
 * @param matrix
 * @param trip_id
 * @returns
 * @description return the first combination of class and fare that represent the cheapest price for a trip_id
 * @description in case of row not found we will [undefined, undefined]
 */
export const getFromOptionsMatriceFirstCoordinateForATripId = (
  matrix: SimpleLegOption[][] | TripOption[][],
  trip_id: string
) => {
  const rowIndex = matrix.findIndex(row =>
    row.find(e => e.trip_id === trip_id)
  );
  if (rowIndex === -1) {
    return [undefined, undefined];
  }
  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const colIndex = matrix[rowIndex].findIndex(e => e.trip_id === trip_id);
  return [rowIndex, colIndex];
};

export const getLegCardOptionsMatrix = createCachedSelector(
  getLegCardById,
  getTrips,
  getTripsSegments,
  getSearch,
  getTripsFares,
  getDisplayedPriceConfig,
  buildLegCardOptionsMatrix
)((_, leg_card_id) => leg_card_id);

export const getLegCardOptionsMatrixForJourney = createCachedSelector(
  getTripIdsByLegCardIdAndJourneyId,
  getTrips,
  getTripsSegments,
  getSearch,
  getTripsFares,
  getDisplayedPriceConfig,
  buildLegCardOptionsMatrix
)((_, leg_card_id, journey_id) => leg_card_id + journey_id);

function buildLegCardOptionsMatrix(
  trip_ids: string[] = [],
  trips: ReturnType<typeof getTrips>,
  segments: ReturnType<typeof getTripsSegments>,
  search: ReturnType<typeof getSearch>,
  fares: ReturnType<typeof getTripsFares>,
  displayed_price_config: ReturnType<typeof getDisplayedPriceConfig>
): TripOptionsMatrix {
  const direction = isReturn(search) ? "return" : "outbound";
  // TODO: UDMV4 build the TripOptionsMatrix from the UDMv4 trip
  // @ts-expect-error TS2538 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  if (isUDMV4(trips[trip_ids[0]])) {
    return [];
  }

  const leg_card_options_sorted_by_price: SimpleLegOption[] = uniqBy(
    trip_ids.map(id => {
      const trip = trips[id];
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const leg = getSearchedLeg(search, trip);
      const leg_segments = getSegmentsFromTripLeg(leg, segments);
      // TODO: UDMV4
      // @ts-expect-error fix class
      const class_xid = leg_segments[0].class.xid;
      // TODO: UDMV4
      // @ts-expect-error fix fare
      const fare_id = leg_segments[0].fare_id;
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const price = getDisplayPrice(trip, direction, displayed_price_config);

      return {
        class_xid,
        fare_id,
        price,
        // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        trip_id: trip.id
      };
    }),
    e => e.class_xid + e.fare_id
  ).sort((a, b) => a.price - b.price);

  const matrix: SimpleLegOption[][] = leg_card_options_sorted_by_price
    .reduce((acc, leg_card_option) => {
      if (acc.length === 0) {
        acc.push([leg_card_option]);
        return acc;
      }

      const class_xid_index = acc.findIndex(
        leg_card_option_list =>
          // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          leg_card_option_list[0].class_xid === leg_card_option.class_xid
      );

      if (class_xid_index >= 0) {
        // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        acc[class_xid_index].push(leg_card_option);
        return acc;
      }

      acc.push([leg_card_option]);
      return acc;
    }, [] as SimpleLegOption[][])
    .map(leg_card_option_list =>
      leg_card_option_list.map(leg_card_option => {
        return {
          class_xid: leg_card_option.class_xid,
          fare_id: leg_card_option.fare_id,
          trip_id: leg_card_option.trip_id
        };
      })
    );
  const list_option = buildClassAndFareData(
    trip_ids,
    segments,
    trips,
    search,
    fares,
    displayed_price_config
  );
  const final_matrix: TripOptionsMatrix = matrix.map(c => {
    return sortBy(
      c.map(f => {
        const fare_options = list_option.find(l => l.trip_id === f.trip_id);
        return fare_options!;
      }),
      f => f?.is_sold_out
    );
  });

  return final_matrix;
}

/**
 *
 * @param price_mode
 * @param user_region
 * @param user_country
 * @param locale
 * @param trip_ids
 * @param trips_segments
 * @param trips
 * @param search
 * @param fares
 * @param trip_options_matrix
 * @param displayed_price_config
 * @TODO: UDMV4 this will be updated
 * @description contient toutes les informations a afficher sur la page ticket option relatif
 * a une class et une fare donne
 */
const buildClassAndFareData = (
  trip_ids: LegCard,
  trips_segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  trips:
    | Record<string, RelatedPublicTrip>
    | Record<string, RelatedPublicTripV4>,
  search: SearchInfo,
  fares: Record<string, PublicFare> | Record<string, PublicFareV4>,
  displayed_price_config: DisplayedPriceConfig
) => {
  return trip_ids.reduce((acc, trip_id) => {
    const trip = trips[trip_id];
    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    const leg = getSearchedLeg(search, trip);

    const direction = isReturn(search) ? "return" : "outbound";

    const price_in_cents = getDisplayPrice(
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      trip,
      direction,
      displayed_price_config
    );

    // TODO: use getDisplayPrice instead of getLocalizedPrice
    // TODO: UDMV4 build it using getDisplayPrice
    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    const prices_per_segment = isUDMV4(trip)
      ? {}
      : // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        trip.prices.reduce(
          (prices_per_segment, price_group) => {
            price_group.segment_ids.forEach(
              segment_id =>
                (prices_per_segment[segment_id] = {
                  price_in_cents: getLocalizedPrice(
                    price_group.prices.breakdown,
                    {
                      include_fees: shouldIncludeFees(displayed_price_config)
                    }
                  )
                })
            );

            return prices_per_segment;
          },
          {} as TripOption["prices_per_segment"]
        );

    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    const is_sold_out = isTripSoldOutHelper(search, trip, trips_segments);
    const sources_names = getLegSourceNamesFromTripPrices(leg, trip);
    const leg_segments = getSegmentsFromTripLeg(leg, trips_segments);

    // For now we get the information of the classes and the fares from the first segment only
    const first_leg_segment = leg_segments[0];
    // TODO: UDMV4 build it using getDisplayPrice
    // @ts-expect-error
    const fare = fares[first_leg_segment.fare_id];

    // TODO: UDMV4 build it using getDisplayPrice
    // @ts-expect-error
    const generic_class_name = first_leg_segment?.class?.name;

    acc.push({
      trip_id,
      // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      fare_id: fare?.id,
      // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      fare_xid: fare?.xid,
      // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      fare_name: fare?.name,
      // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      terms: fare?.terms ?? [],
      // TODO: UDMV4 build it using getDisplayPrice
      // @ts-expect-error
      class_xid: first_leg_segment.class?.xid,
      class_name: generic_class_name,
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      amenities: first_leg_segment.vehicle.amenities as TripOption["amenities"],
      price_in_cents,
      is_sold_out,
      sources_names,
      prices_per_segment,
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      journey_id: trip.journey_id
    });

    return acc;
  }, [] as TripOptionsList);
};
